// Delivery view (pipeline-focused). Full-window mode reached via
// /sessions/:id/delivery. Left = feature list. Right = Migration Unit
// pipeline cards. CSV import → wizard → MUs come from the column or the
// chosen mode.

import React, { useCallback, useMemo, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { ArrowLeft, Download, Globe, Sparkles, FolderInput } from 'lucide-react';
import { useSessionFeatures } from '../hooks/useSessionFeatures';
import { useDeliveryAnalysis } from '../hooks/useDeliveryAnalysis';
import { useFeatureSpecs } from '../hooks/useFeatureSpecs';
import DeliveryFeatureList from './DeliveryFeatureList';
import DeliveryPipeline from './DeliveryPipeline';
import DeliveryCsvImportModal from './DeliveryCsvImportModal';
import SpecFolderImportModal from './SpecFolderImportModal';
import AppBrand from './AppBrand';
import { combineFeatures, computeStats, isStale } from '../utils/deliveryAnalysisModel';
import '../styles/DeliveryView.css';

const DeliveryView: React.FC = () => {
  const { sessionId } = useParams<{ sessionId: string }>();
  const sessionFeatures = useSessionFeatures(sessionId || null);
  const delivery = useDeliveryAnalysis({ sessionId: sessionId || null });
  const specs = useFeatureSpecs(sessionId || null);

  const allFeatures = useMemo(
    () => combineFeatures(
      sessionFeatures.discoveryFeatures.features,
      delivery.payload.importedFeatures,
    ),
    [sessionFeatures.discoveryFeatures.features, delivery.payload.importedFeatures],
  );
  const hasAnyFeatures = allFeatures.length > 0;
  const stats = useMemo(() => computeStats(allFeatures, delivery.payload), [allFeatures, delivery.payload]);

  const [selectedFeatureId, setSelectedFeatureId] = useState<string | null>(null);
  const [importOpen, setImportOpen] = useState(false);
  const [specFolderOpen, setSpecFolderOpen] = useState(false);

  const stale = isStale(
    delivery.payload,
    sessionFeatures.discoveryFeatures.extractedAt,
    delivery.payload.basedOnImportHash,
  );

  const handleRunAiGrouping = useCallback(async () => {
    await delivery.group();
  }, [delivery]);

  if (!sessionId) {
    return (
      <div className="dv-shell" data-state="error">
        <div className="dv-empty">Missing session id in URL.</div>
      </div>
    );
  }

  return (
    <div className="dv-shell">
      <header className="dv-header">
        <div className="dv-header-brand">
          <AppBrand compact />
          <span className="dv-divider" />
          <Link to={`/sessions/${sessionId}`} className="dv-back-link" title="Back to session">
            <ArrowLeft size={16} />
            <span>Back to session</span>
          </Link>
          <span className="dv-divider" />
          <Globe size={16} />
          <span className="dv-title">Delivery view</span>
          <span className="dv-session-id">{sessionId}</span>
        </div>
        <div className="dv-header-stats">
          <span><strong>{stats.muCount}</strong> MUs</span>
          <span>·</span>
          <span><strong>{stats.visibleFeatureCount}</strong> features{stats.totalEffort > 0 && ` · ${stats.totalEffort} pts`}</span>
          <span>·</span>
          <span><strong>{stats.musWithFlow}</strong> with process map</span>
          {stale && <span className="dv-status dv-status--stale">source changed since grouping</span>}
        </div>
        <div className="dv-header-actions">
          <button
            type="button"
            className="dv-btn dv-btn--ghost"
            onClick={() => delivery.score()}
            disabled={delivery.scoring || !hasAnyFeatures}
            title="Score every visible feature with AI (effort, value, tags). Manual overrides are preserved."
          >
            {delivery.scoring ? <span className="dv-spinner" /> : <Sparkles size={13} />}
            {delivery.scoring ? 'Scoring…' : 'Score with AI'}
          </button>
          <button
            type="button"
            className="dv-btn dv-btn--ghost"
            onClick={() => setSpecFolderOpen(true)}
            disabled={!hasAnyFeatures}
            title="Bulk-import specs from a folder of markdown files (matched by GUID)"
          >
            <FolderInput size={13} /> Import specs
          </button>
          <button
            type="button"
            className="dv-btn dv-btn--ghost"
            onClick={() => { window.location.href = `/api/sessions/${sessionId}/feature-specs/export.zip`; }}
            disabled={!hasAnyFeatures}
            title="Download all MUs + specs as a zip"
          >
            <Download size={13} /> Export
          </button>
        </div>
      </header>

      {delivery.error && (
        <div className="dv-error-banner" role="alert">{delivery.error}</div>
      )}

      <main className="dv-main">
        {!hasAnyFeatures ? (
          <div className="dv-empty-full">
            <div className="dv-empty-icon">◇</div>
            <h2>No features yet</h2>
            <p>Run Discovery on this session to extract features, or import a CSV to get started.</p>
            <button
              type="button"
              className="dv-btn dv-btn--primary dv-btn--lg"
              onClick={() => setImportOpen(true)}
            >
              + Import CSV
            </button>
          </div>
        ) : (
          <>
            <DeliveryFeatureList
              features={allFeatures}
              payload={delivery.payload}
              selectedFeatureId={selectedFeatureId}
              onSelect={setSelectedFeatureId}
              onImportClick={() => setImportOpen(true)}
              onPatch={delivery.patch}
            />
            <DeliveryPipeline
              sessionId={sessionId}
              features={allFeatures}
              payload={delivery.payload}
              specsPayload={specs.payload}
              selectedFeatureId={selectedFeatureId}
              onSelect={setSelectedFeatureId}
              onPatch={delivery.patch}
              onRunAiGrouping={handleRunAiGrouping}
              grouping={delivery.grouping}
            />
          </>
        )}
      </main>

      <DeliveryCsvImportModal
        isOpen={importOpen}
        onClose={() => setImportOpen(false)}
        onImport={async (rows, opts) => delivery.importCsv(rows, opts)}
        importing={delivery.importing}
      />
      <SpecFolderImportModal
        isOpen={specFolderOpen}
        onClose={() => { setSpecFolderOpen(false); specs.refresh(); }}
        features={allFeatures}
        specsPayload={specs.payload}
        onSave={specs.save}
      />
    </div>
  );
};

export default DeliveryView;
